import React, { useState, useEffect } from 'react';
import { useToast } from '../../context/ToastContext';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Navbar from '../../components/Navbar';
import Modal from '../../components/Modal';
import api from '../../utils/api';

const EmailSettings = () => {
  const { showToast } = useToast();
  const [emailSettings, setEmailSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [descModal, setDescModal] = useState({ isOpen: false, text: '' });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const emailRes = await api.get('/admin/email-settings');
      if (emailRes.data && emailRes.data.success) setEmailSettings(emailRes.data.data);
    } catch (error) {
      console.error('Error fetching email settings:', error);
      showToast('Failed to load email settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatCamelCase = (text) => {
    const result = text.replace(/([A-Z])/g, " $1");
    return result.charAt(0).toUpperCase() + result.slice(1);
  };

  const formatCategory = (category) => {
    if (!category) return '';
    return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
  };

  const handleToggleEmailSetting = async (templateName, currentStatus, isCritical) => {
    if (isCritical) {
      showToast("Critical emails cannot be disabled.", "warning");
      return;
    }
    
    try {
      const res = await api.put(`/admin/email-settings/${templateName}`, { isEnabled: !currentStatus });
      if (res.data.success) {
        setEmailSettings(prev => prev.map(s => s.templateName === templateName ? { ...s, isEnabled: !currentStatus } : s));
        showToast('Email setting updated', 'success');
      }
    } catch (error) {
      showToast('Error toggling email setting', 'error');
    }
  };

  // Sort email settings: critical ones first, then alphabetical or as-is
  const sortedSettings = [...emailSettings].sort((a, b) => {
    if (a.isCritical && !b.isCritical) return -1;
    if (!a.isCritical && b.isCritical) return 1;
    return 0;
  });

  if (loading) return (
    <>
      <Navbar />
      <div className="p-8 text-center text-gray-500">Loading Email Settings...</div>
    </>
  );

  return (
    <>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Email Settings</h1>
            <p className="mt-2 text-gray-600">Manage the platform's automated email notifications.</p>
          </div>
        </div>

        <Card>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email Event</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedSettings.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">No email settings found. Please run the email seed script.</td>
                  </tr>
                ) : (
                  sortedSettings.map((setting) => (
                    <tr key={setting.templateName} className={!setting.isEnabled ? 'bg-gray-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{formatCamelCase(setting.templateName)}</div>
                        {setting.isCritical && <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full mt-1 inline-block">Critical</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatCategory(setting.category)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                        <div className="flex items-center space-x-2">
                          <span className="truncate max-w-[200px]">{setting.description || 'N/A'}</span>
                          {setting.description && (
                            <button 
                              onClick={() => setDescModal({ isOpen: true, text: setting.description })}
                              className="text-primary-600 hover:text-primary-800 focus:outline-none flex-shrink-0"
                              title="View Full Description"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          setting.isEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {setting.isEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button 
                          type="button"
                          onClick={() => handleToggleEmailSetting(setting.templateName, setting.isEnabled, setting.isCritical)} 
                          disabled={setting.isCritical}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 ${setting.isCritical ? 'bg-gray-200 cursor-not-allowed opacity-50' : setting.isEnabled ? 'bg-indigo-600' : 'bg-gray-200'}`}
                        >
                          <span className="sr-only">{setting.isEnabled ? 'Disable' : 'Enable'} setting</span>
                          <span
                            aria-hidden="true"
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${setting.isEnabled ? 'translate-x-5' : 'translate-x-0'}`}
                          />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Modal for Description */}
        <Modal
          isOpen={descModal.isOpen}
          onClose={() => setDescModal({ isOpen: false, text: '' })}
          title="Description"
        >
          <p className="text-gray-700 leading-relaxed text-lg">{descModal.text}</p>
          <div className="mt-6 flex justify-end">
            <Button variant="primary" onClick={() => setDescModal({ isOpen: false, text: '' })}>Close</Button>
          </div>
        </Modal>

      </div>
    </>
  );
};

export default EmailSettings;
